'use client';

import React, { useState, useEffect, useRef } from 'react';
import TrainerCard from './TrainerCard';

const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

// Updated Data Layer: Content derived from provided text
const featuredTrainers = [
  {
    name: 'Sarah Ahmed',
    title: 'Senior EMS Specialist',
    bio: 'Specializes in metabolic tracking and high-intensity Miha Bodytec protocols for rapid body transformation.',
    imageUrl: '/about/trainer1.webp',
  },
  {
    name: 'Zain Malik',
    title: 'Certified EMS Physiologist',
    bio: 'Expert in post-natal recovery and deep core reconstruction using targeted EMS pulse modulation.',
    imageUrl: '/about/trainer2.webp',
  },
  {
    name: 'Ayesha Khan',
    title: 'Performance & Mobility Lead',
    bio: 'Focuses on executive wellness and mobility protocols, ensuring elite results within a 20-minute window.',
    imageUrl: '/about/trainer3.webp',
  },
];

export default function TrainersSection() {
  const [activeTrainerIndex, setActiveTrainerIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setActiveTrainerIndex(index);
          }
        });
      },
      {
        root: container,
        threshold: 0.6,
      }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      if (container) observer.disconnect();
    };
  }, []);

  return (
    <section className="py-24 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header - Information Layer */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-extrabold mb-4 uppercase tracking-tight"
            style={{ color: GOLD_ACCENT, textShadow: `0 0 10px ${SHADOW_GOLD}` }}
          >
            Your Dedicated Specialists
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-gray-400">
            Every session is supervised one-on-one by a trained EMS specialist who monitors your progress and masters the technology for you.
          </p>
        </div>

        {/* Mobile horizontal scroll - Interaction Layer */}
        <div 
          ref={scrollContainerRef}
          className="md:hidden overflow-x-auto py-12 scrollbar-hide" 
        >
          <div className="flex gap-6 snap-x snap-mandatory px-[calc(50%-140px)]"> 
            {featuredTrainers.map((trainer, index) => {
              const isActive = activeTrainerIndex === index;

              return (
                <div
                  key={trainer.name}
                  ref={(el) => (cardRefs.current[index] = el)}
                  data-index={index}
                  className={`
                    snap-center min-w-[280px] max-w-[280px]
                    transition-all duration-500 ease-out relative
                    ${isActive ? 'scale-110 opacity-100 z-10' : 'scale-90 opacity-40 blur-[2px] z-0'}
                  `}
                >
                  <div className="w-full min-h-[480px] mx-auto flex flex-col relative">
                     {/* Interaction Feedback: The Glow Effect */}
                     <div 
                        className={`absolute inset-0 rounded-xl transition-opacity duration-500 pointer-events-none border-2 border-[#faaf40] ${isActive ? 'opacity-100 shadow-[0_0_30px_rgba(250,175,64,0.5)]' : 'opacity-0'}`} 
                        style={{ zIndex: 20 }}
                     />
                     <TrainerCard trainer={trainer} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop Grid - Structure Layer */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
          {featuredTrainers.map((trainer) => (
            <TrainerCard key={trainer.name} trainer={trainer} />
          ))}
        </div>

        {/* Bottom CTA content from your text */}
        <div className="mt-20 text-center border-t border-zinc-800 pt-12">
           <p className="text-gray-500 italic max-w-3xl mx-auto">
             "Our trainers don't just guide you for an hour; they provide technology, tracking, and accountability compressed into a session that actually fits your life".
           </p>
        </div>
      </div>
    </section>
  );
}